import { browserHelpers, dateTimeHelpers } from '@approvalmax/utils';
import { KeyboardEventHandler, RefObject, useCallback } from 'react';
import { useToggle } from 'react-use';

import type { HTMLTextFieldElement, TextFieldProps } from '../TextField/TextField.types';
import { formatTextFieldValue, parseEnteredTextFieldValue, parseValueToDate } from './TimePicker.helpers';
import { TimePickerProps } from './TimePicker.types';

/**
 * Hook for handling the value in the text field
 */
export const useTextFieldValue = (
    props: Pick<ReturnType<typeof useTimeValue>, 'changeTimePickerValue' | 'timeValue'>
) => {
    const { timeValue, changeTimePickerValue } = props;

    const textFieldValue = formatTextFieldValue(timeValue);

    const handleTextFieldChange = useCallback<NonNullable<TextFieldProps['onChange']>>(
        (enteredValue) => {
            const parsedDate = parseEnteredTextFieldValue(enteredValue);

            changeTimePickerValue(parsedDate);
        },
        [changeTimePickerValue]
    );

    return {
        textFieldValue,
        handleTextFieldChange,
    };
};

/**
 * Hook for working with time value
 */
export const useTimeValue = (
    props: Pick<TimePickerProps, 'value' | 'noUtc'> &
        Required<Pick<TimePickerProps, 'onChange'>> & { inputRef: RefObject<HTMLTextFieldElement> }
) => {
    const { value, onChange, noUtc, inputRef } = props;

    const timeValue = noUtc ? parseValueToDate(value) : dateTimeHelpers.convertLocalToUTC(value);

    const changeTimePickerValue = useCallback(
        (changeValue?: Date) => {
            inputRef.current?.focus();
            onChange(noUtc ? changeValue : dateTimeHelpers.convertUTCToLocal(changeValue));
        },
        [onChange, noUtc, inputRef]
    );

    return {
        timeValue,
        changeTimePickerValue,
    };
};

/**
 * Hook for handling user actions
 */
export const useCallbackActions = (props: Pick<TimePickerProps, 'onOpen' | 'initOpen' | 'initFocus'>) => {
    const { onOpen, initOpen, initFocus } = props;

    const [open, toggleOpen] = useToggle(Boolean(initOpen));
    const [focus, toggleFocus] = useToggle(Boolean(initFocus));
    const [hover, toggleHover] = useToggle(false);

    const handleOpen = useCallback<Required<TimePickerProps>['onOpen']>(
        (nextOpen, event) => {
            toggleOpen(nextOpen);
            onOpen?.(nextOpen, event);
        },
        [onOpen, toggleOpen]
    );

    const handleMouseEnter = useCallback(() => toggleHover(true), [toggleHover]);
    const handleMouseLeave = useCallback(() => toggleHover(false), [toggleHover]);

    const handleClick = useCallback<Required<TimePickerProps>['onClick']>(
        (event) => {
            // prevents opening Safari's native picker
            if (browserHelpers.isSafari()) {
                event.preventDefault();
            }

            // If it's not open but focused, open it
            !open && handleOpen(true);
        },
        [handleOpen, open]
    );

    const handleKeyDown = useCallback<KeyboardEventHandler<HTMLTextFieldElement>>(
        (event) => {
            if (event.key !== ' ') return;

            // prevent opening browser's calendar
            event.preventDefault();

            if (!open) handleOpen(true);
        },
        [handleOpen, open]
    );

    return {
        open,
        handleOpen,
        focus,
        handleClick,
        handleKeyDown,
        hover,
        handleMouseEnter,
        handleMouseLeave,
        toggleOpen,
        toggleFocus,
    };
};
